import { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

interface LoadMoreTriggerProps {
  onLoadMore: () => void;
  isLoading: boolean;
  hasMore?: boolean;
}

export function LoadMoreTrigger({
  onLoadMore,
  isLoading,
  hasMore,
}: LoadMoreTriggerProps) {
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    const currentTrigger = triggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [hasMore, isLoading, onLoadMore]);

  if (!hasMore && !isLoading) return null;

  return (
    <LoadMoreContainer ref={triggerRef}>
      {isLoading && (
        <LoadingIndicator>
          {/* Translation Needed */}
          <LoadingText>Loading more</LoadingText>
          <Spinner />
        </LoadingIndicator>
      )}
    </LoadMoreContainer>
  );
}

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  margin-top: 40px;
  min-height: 60px;
`;

const LoadingIndicator = styled.div`
  align-items: center;
  color: var(--grey-700);
  display: flex;
  gap: 12px;
`;

const LoadingText = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.div`
  animation: ${spin} 0.8s linear infinite;
  border: 2px solid var(--grey-200);
  border-radius: 50%;
  border-top-color: var(--grey-700);
  height: 16px;
  width: 16px;
`;
